// client-engagement-system.js - Tier 3 Solution Page
export const content = {
  heroSubtitle: "Engagement is the foundation of retention. A systematic approach to keeping clients connected, informed, and loyal—without drowning in manual touchpoints.",

  problemSection: {
    title: "The Engagement Gap",
    html: `
      <p>Here's a truth most agents don't want to face: your clients aren't thinking about you. Between annual enrollments, you're not on their radar. They're living their lives, and unless something goes wrong with their coverage, you don't cross their mind.</p>

      <p>That's dangerous. When a client doesn't think about you, they don't think about the relationship. When a competitor calls during AEP, there's no loyalty buffer. When they have a question, they might call the carrier instead of you. Slowly, the relationship erodes through neglect.</p>

      <p>Client engagement systems solve this by maintaining presence systematically. Not through spam or unwanted contact—through valuable, appropriate touchpoints that keep the relationship alive between major interactions.</p>
    `
  },

  features: [
    {
      icon: "clock",
      title: "Touchpoint Scheduling",
      description: "Automated scheduling of appropriate touchpoints: quarterly check-ins, birthday acknowledgments, policy anniversaries, and seasonal outreach. Never wonder 'when did I last talk to this client?'"
    },
    {
      icon: "users",
      title: "Personalized Messaging",
      description: "Templates that include client-specific details automatically. Mass communication that doesn't feel mass. Personal connection without personal time investment."
    },
    {
      icon: "bell",
      title: "Intelligent Triggers",
      description: "Engagement triggered by events, not just calendar. Plan changes, life events, service issues—all create appropriate touchpoint opportunities."
    },
    {
      icon: "chart",
      title: "Engagement Scoring",
      description: "Track engagement levels across your book. Who's highly engaged? Who's going cold? Identify clients needing attention before they become retention risks."
    },
    {
      icon: "zap",
      title: "Multi-Channel Reach",
      description: "Email, text, phone, mail—reach clients through their preferred channels. Some prefer calls, others prefer texts. The system adapts."
    },
    {
      icon: "target",
      title: "Value-Add Content",
      description: "Engagement that provides value: Medicare updates, benefit reminders, wellness tips. Give them a reason to appreciate hearing from you."
    }
  ],

  featuresTitle: "Systematic Client Engagement",

  mainContent: `
    <h2>What Is Client Engagement?</h2>

    <p>Engagement is the ongoing interaction between you and your clients outside of enrollment events. It includes:</p>

    <ul>
      <li><strong>Proactive communication:</strong> Reaching out before they need to reach you</li>
      <li><strong>Value delivery:</strong> Providing information, updates, and reminders that help them</li>
      <li><strong>Relationship maintenance:</strong> Personal acknowledgments that show you care</li>
      <li><strong>Availability:</strong> Being there when they need something</li>
    </ul>

    <p>Good engagement creates clients who think of you regularly, trust you instinctively, and wouldn't consider switching without talking to you first.</p>

    <h2>The Engagement-Retention Connection</h2>

    <p>Research and practical experience show clear patterns:</p>

    <ul>
      <li>Clients with regular contact are 2-3x more likely to stay</li>
      <li>Clients who feel "served" are resistant to competitor marketing</li>
      <li>Engagement creates referrals (happy clients recommend you)</li>
      <li>Problems are caught earlier when communication is regular</li>
    </ul>

    <p>Engagement isn't just nice to have—it's the foundation retention is built on.</p>

    <h2>The Engagement Calendar</h2>

    <p>Pro IRP structures engagement around a systematic calendar:</p>

    <h3>Scheduled Touchpoints</h3>
    <ul>
      <li><strong>Quarterly check-ins:</strong> Brief "how's everything going?" contacts</li>
      <li><strong>Birthday acknowledgments:</strong> Personal recognition</li>
      <li><strong>Policy anniversaries:</strong> Relationship milestone acknowledgment</li>
      <li><strong>Seasonal outreach:</strong> Flu shot reminders, wellness tips</li>
      <li><strong>Pre-enrollment:</strong> AEP preparation communications</li>
    </ul>

    <h3>Event-Triggered Touchpoints</h3>
    <ul>
      <li><strong>Plan changes:</strong> When something affects their coverage</li>
      <li><strong>Service resolution:</strong> Follow-up after helping with an issue</li>
      <li><strong>Life events:</strong> When you learn of changes in their situation</li>
      <li><strong>Re-engagement:</strong> When contact has been too long</li>
    </ul>

    <h2>Personalization at Scale</h2>

    <p>Effective engagement feels personal, but you can't write individual messages for hundreds of clients. The system bridges this through:</p>

    <h3>Dynamic Content</h3>
    <ul>
      <li>Client name and details inserted automatically</li>
      <li>Plan-specific information included</li>
      <li>Recent interaction history referenced</li>
      <li>Relevant details surfaced naturally</li>
    </ul>

    <h3>Conditional Messaging</h3>
    <ul>
      <li>Different messages for different situations</li>
      <li>New client vs. established client variations</li>
      <li>MA vs. Med Supp appropriate content</li>
      <li>Issue-specific vs. routine touchpoints</li>
    </ul>

    <h3>Channel Matching</h3>
    <ul>
      <li>Respect client communication preferences</li>
      <li>Email for detailed information</li>
      <li>Text for quick acknowledgments</li>
      <li>Phone for conversations that need voice</li>
    </ul>

    <h2>Engagement Scoring</h2>

    <p>Not all clients are equally engaged. The system tracks engagement levels:</p>

    <h3>Highly Engaged (Score 80-100)</h3>
    <ul>
      <li>Regular communication both directions</li>
      <li>Responds to outreach promptly</li>
      <li>Reaches out proactively when questions arise</li>
      <li>Refers friends and family</li>
    </ul>

    <h3>Moderately Engaged (Score 50-79)</h3>
    <ul>
      <li>Responds to most outreach</li>
      <li>Occasionally reaches out</li>
      <li>Attends annual reviews</li>
    </ul>

    <h3>Low Engagement (Score 0-49)</h3>
    <ul>
      <li>Rarely responds to outreach</li>
      <li>Doesn't return calls</li>
      <li>No proactive contact in 6+ months</li>
      <li>Higher retention risk</li>
    </ul>

    <p>Low engagement scores trigger re-engagement workflows and increased retention attention.</p>

    <h2>Value-Add Content</h2>

    <p>The best engagement provides value, not just contact. Content that helps:</p>

    <ul>
      <li><strong>Medicare updates:</strong> Changes that affect them</li>
      <li><strong>Benefit reminders:</strong> "Did you know your plan includes..."</li>
      <li><strong>Wellness information:</strong> Flu shot reminders, preventive care</li>
      <li><strong>Local resources:</strong> Senior services in their area</li>
      <li><strong>Administrative help:</strong> EOB explanations, claim assistance</li>
    </ul>

    <p>When clients receive value from your communications, they appreciate hearing from you instead of tuning you out.</p>

    <h2>Measuring Engagement Effectiveness</h2>

    <p>Track what's working:</p>

    <ul>
      <li><strong>Open rates:</strong> Are they reading your emails?</li>
      <li><strong>Response rates:</strong> Are they engaging with your outreach?</li>
      <li><strong>Engagement score trends:</strong> Is engagement improving or declining?</li>
      <li><strong>Retention correlation:</strong> Do highly engaged clients stay longer?</li>
    </ul>

    <p>Use this data to refine your approach. More of what works, less of what doesn't.</p>

    <h2>Getting Started</h2>

    <p>Implementation is straightforward:</p>

    <ol>
      <li><strong>Configure touchpoint schedule:</strong> Choose your engagement cadence</li>
      <li><strong>Customize templates:</strong> Adapt messaging to your voice</li>
      <li><strong>Set channel preferences:</strong> Email, text, call for different touchpoints</li>
      <li><strong>Launch:</strong> System begins scheduling and tracking</li>
    </ol>

    <p>Most agents see engagement improvement within the first month. Retention impact follows.</p>
  `,

  faqs: [
    {
      question: "How often should I engage with clients?",
      answer: "At minimum: quarterly check-ins plus birthday and policy anniversary acknowledgments. That's 5+ touchpoints per year. More frequent is better for high-value or higher-risk clients. The key is consistency—sporadic engagement is worse than none."
    },
    {
      question: "Won't clients be annoyed by too much contact?",
      answer: "Not if the contact provides value. Clients appreciate relevant information, helpful reminders, and appropriate acknowledgments. They're annoyed by spam. The difference is whether your engagement helps them or just serves you."
    },
    {
      question: "How do I personalize engagement at scale?",
      answer: "Templates with dynamic fields that insert client-specific details automatically. Conditional content that adapts to their situation (MA vs. Med Supp, new vs. established). Channel matching to their preferences. Mass outreach that doesn't feel mass."
    },
    {
      question: "What is engagement scoring?",
      answer: "A measure of how connected a client is to you based on: response rates, proactive contact, meeting attendance, referral activity. Low engagement scores indicate clients at risk of drifting away and trigger re-engagement workflows."
    },
    {
      question: "How do I know if my engagement is working?",
      answer: "Track open rates, response rates, engagement score trends, and most importantly—retention correlation. If highly engaged clients stay longer (they almost always do), your engagement is working. Refine based on what the data shows."
    }
  ],

  sources: [
    {
      title: "Medicare Beneficiary Communication",
      url: "https://www.cms.gov/Medicare/Health-Plans/ManagedCareMarketing",
      publisher: "CMS",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
